// ADR 15.10.26d §2 — the install UI's own error boundary.
//
// The install UI is optional, so a render throw inside it should cost the
// learner the install entry, not the page: below `RootErrorBoundary`, this
// catches it, reports it with the device it happened on, and renders nothing.
// A boundary catches only its descendants, so each install export wraps its
// renamed body rather than wrapping inside its own JSX — that would miss the
// component's own hooks (`useInstallAffordance`'s UA parse is the likeliest
// field failure).
//
// Reports once per page load per surface: a module-level set, not instance
// state, because the header and route slots remount on navigation and
// StrictMode double-mounts. An `onClick` throw is not a render error and never
// reaches here (Risk 6).
//
// The one `field/` module that reads `promptAvailable()`; nothing in
// `pwa/installPrompt.ts`'s import chain imports it, so that is no cycle.

import { Component, type ErrorInfo, type ReactNode } from 'react'

import { promptAvailable } from '../../pwa/installPrompt'
import { deliver } from './delivery'
import { caughtFingerprint, deviceRecord, deviceTags, readDeviceInputs, withHints } from './deviceRecord'

type InstallBoundarySurface = 'strip' | 'header' | 'door' | 'account' | 'top_slot'

const RENDER_KIND = 'install.render'
const reported = new Set<InstallBoundarySurface>()

/** Test-only: the once-per-surface set is module-level. */
export function resetInstallBoundaryForTests(): void {
  reported.clear()
}

async function reportRenderFailure(error: unknown, surface: InstallBoundarySurface, componentStack: string): Promise<void> {
  try {
    if (reported.has(surface)) return
    reported.add(surface)
    const r = await withHints(deviceRecord(readDeviceInputs(promptAvailable())))
    deliver({
      kind: RENDER_KIND,
      transport: 'caught',
      error,
      fingerprint: caughtFingerprint(RENDER_KIND, r),
      tags: { ...deviceTags(r), 'install.surface': surface },
      contexts: { device_record: r, react: { componentStack } },
    })
  } catch {
    // Total by contract (§0): a report never throws into the UI it observes.
  }
}

interface InstallBoundaryProps {
  surface: InstallBoundarySurface
  children: ReactNode
}

export class InstallBoundary extends Component<InstallBoundaryProps, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    void reportRenderFailure(error, this.props.surface, info.componentStack ?? '')
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children
  }
}
